import type { FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { telegramLoginURL, unlock, type SessionMode } from "../features/session/api";
import { unlockSchema, type UnlockForm } from "../features/session/model";
import { humanError } from "../shared/api/client";

const loginErrors: Record<string, string> = {
  unauthorized: "Telegram did not confirm the sign-in. Try again.",
  expired: "The sign-in took too long. Start again.",
  state: "The sign-in was interrupted. Start again.",
};

interface UnlockPageProps {
  onUnlocked: () => void;
  mode?: SessionMode;
}

export function UnlockPage({ onUnlocked, mode = "code" }: UnlockPageProps) {
  const [searchParams] = useSearchParams();
  const loginError = loginErrors[searchParams.get("error") ?? ""] ?? "";
  const form = useForm<UnlockForm>({ resolver: zodResolver(unlockSchema), defaultValues: { code: "" } });
  const mutation = useMutation({
    mutationFn: unlock,
    onSuccess: () => {
      form.reset();
      onUnlocked();
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const field = event.currentTarget.elements.namedItem("code");
    const value = field instanceof HTMLInputElement ? field.value : "";
    const result = unlockSchema.safeParse({ code: value });
    if (!result.success) {
      form.setError("code", { message: result.error.issues[0]?.message ?? "Enter the private preview code." });
      return;
    }
    form.clearErrors("code");
    mutation.mutate(result.data.code);
  }

  if (mode === "telegram") {
    return (
      <section className="gate view" aria-labelledby="gate-title">
        <div className="gate-copy">
          <p className="eyebrow">Ledgerly</p>
          <h1 id="gate-title">Your money,<br />without the fog.</h1>
          <p className="lede">Accounts, balances and money flow from one double-entry journal. Sign in with the Telegram account that owns your book.</p>
        </div>
        <div className="access-card">
          <div>
            <span className="step-number">01</span>
            <h2>Sign in</h2>
            <p>Telegram confirms who you are; Ledgerly never sees your Telegram password. Your session stays in a secure cookie.</p>
          </div>
          <a className="primary-button" href={telegramLoginURL("/")} data-testid="telegram-login">
            <span>Continue with Telegram</span><span aria-hidden="true">→</span>
          </a>
          <p className="form-error" role="alert">{loginError}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="gate view" aria-labelledby="gate-title">
      <div className="gate-copy">
        <p className="eyebrow">Private MVP preview</p>
        <h1 id="gate-title">Your money,<br />without the fog.</h1>
        <p className="lede">Track income and spending against one clear monthly number. This web client exercises the same Ledgerly API the iPhone app will use.</p>
      </div>
      <form className="access-card" onSubmit={submit}>
        <div>
          <span className="step-number">01</span>
          <h2>Unlock your workspace</h2>
          <p>Enter the private preview code. It stays in this request and is never saved by the browser.</p>
        </div>
        <label>
          Access code
          <input
            {...form.register("code")}
            type="password"
            autoComplete="current-password"
            required
            placeholder="••••••••••••"
          />
        </label>
        <button className="primary-button" type="submit" disabled={mutation.isPending}>
          <span>{mutation.isPending ? "Opening…" : "Open Ledgerly"}</span><span aria-hidden="true">→</span>
        </button>
        <p className="form-error" role="alert">{form.formState.errors.code?.message ?? (mutation.error ? humanError(mutation.error) : "")}</p>
      </form>
    </section>
  );
}
